/**
 * ImageUploadButton Component
 *
 * Button overlay for uploading/changing event hero images.
 * Supports both file upload and URL entry modes.
 *
 * Features:
 * - Click to open upload/URL modal
 * - Drag-and-drop file support
 * - Preview of selected image
 * - Clear/remove image option
 */

import React, { useState, useRef, useCallback } from 'react';
import { Upload, Link, X, Check, Loader2, Image as ImageIcon, Trash2 } from 'lucide-react';

interface ImageUploadButtonProps {
  /** Current image URL (if any) */
  currentImageUrl: string | null;
  /** Callback when image URL changes */
  onImageChange: (url: string | null) => Promise<void>;
  /** Whether the component is in a loading state */
  isLoading?: boolean;
  /** Whether to show as overlay button (for hero image) */
  overlayMode?: boolean;
  /** Whether user is in guest view (read-only) */
  isGuestView?: boolean;
}

type UploadMode = 'file' | 'url';

export const ImageUploadButton: React.FC<ImageUploadButtonProps> = ({
  currentImageUrl,
  onImageChange,
  isLoading = false,
  overlayMode = true,
  isGuestView = false,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [mode, setMode] = useState<UploadMode>('url');
  const [urlInput, setUrlInput] = useState('');
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Don't render anything in guest view
  if (isGuestView) {
    return null;
  }

  const handleOpenModal = useCallback(() => {
    setIsModalOpen(true);
    setUrlInput(currentImageUrl || '');
    setPreviewUrl(currentImageUrl);
    setError(null);
  }, [currentImageUrl]);

  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false);
    setUrlInput('');
    setPreviewUrl(null);
    setError(null);
  }, []);

  const handleUrlChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const url = e.target.value;
    setUrlInput(url);
    setError(null);

    // Preview the URL if it looks valid
    if (url && (url.startsWith('http://') || url.startsWith('https://'))) {
      setPreviewUrl(url);
    } else {
      setPreviewUrl(null);
    }
  }, []);

  const handleSave = useCallback(async () => {
    if (isSaving) return;

    const finalUrl = mode === 'url' ? urlInput.trim() : previewUrl;

    // Validate URL if provided
    if (finalUrl && !finalUrl.startsWith('http://') && !finalUrl.startsWith('https://')) {
      setError('Please enter a valid URL starting with http:// or https://');
      return;
    }

    setIsSaving(true);
    setError(null);

    try {
      await onImageChange(finalUrl || null);
      handleCloseModal();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save image');
    } finally {
      setIsSaving(false);
    }
  }, [mode, urlInput, previewUrl, isSaving, onImageChange, handleCloseModal]);

  const handleRemoveImage = useCallback(async () => {
    if (isSaving) return;

    setIsSaving(true);
    setError(null);

    try {
      await onImageChange(null);
      handleCloseModal();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to remove image');
    } finally {
      setIsSaving(false);
    }
  }, [isSaving, onImageChange, handleCloseModal]);

  const handleFileSelect = useCallback((file: File) => {
    // Validate file type
    if (!file.type.startsWith('image/')) {
      setError('Please select an image file (PNG, JPG, GIF, WebP)');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setError('Image file is too large. Maximum size is 5MB.');
      return;
    }

    // For now, we'll use a data URL for preview
    // In production, this would upload to a storage service and return a URL
    const reader = new FileReader();
    reader.onload = (e) => {
      const dataUrl = e.target?.result as string;
      setPreviewUrl(dataUrl);
      setError(null);
    };
    reader.onerror = () => {
      setError('Failed to read image file');
    };
    reader.readAsDataURL(file);
  }, []);

  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      handleFileSelect(files[0]);
    }
  }, [handleFileSelect]);

  const handleFileInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      handleFileSelect(files[0]);
    }
  }, [handleFileSelect]);

  // Render the trigger button
  const renderTriggerButton = () => {
    if (overlayMode) {
      return (
        <button
          onClick={handleOpenModal}
          disabled={isLoading}
          className="absolute bottom-3 right-3 flex items-center gap-1.5 px-3 py-2 bg-black/70 hover:bg-black/90 text-white text-xs font-medium rounded-lg backdrop-blur-sm transition-all border border-white/20 hover:border-white/40 min-h-[36px] disabled:opacity-50 disabled:cursor-not-allowed"
          title={currentImageUrl ? 'Change image' : 'Add image'}
        >
          {isLoading ? (
            <Loader2 size={14} className="animate-spin" />
          ) : (
            <Upload size={14} />
          )}
          <span>{currentImageUrl ? 'Change' : 'Add Image'}</span>
        </button>
      );
    }

    return (
      <button
        onClick={handleOpenModal}
        disabled={isLoading}
        className="flex items-center gap-2 px-4 py-2 bg-gradient-to-br from-dark-surface/95 to-dark-bg/85 backdrop-blur-sm border border-dark-border/30 rounded-xl text-sm font-medium text-dark-text hover:bg-dark-surface/50 transition-all duration-150 min-h-[44px] disabled:opacity-50 disabled:cursor-not-allowed"
        title={currentImageUrl ? 'Change image' : 'Add image'}
      >
        {isLoading ? (
          <Loader2 size={16} className="animate-spin" />
        ) : (
          <ImageIcon size={16} />
        )}
        <span>{currentImageUrl ? 'Change Image' : 'Add Image'}</span>
      </button>
    );
  };

  return (
    <>
      {renderTriggerButton()}

      {/* Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/70 backdrop-blur-sm"
            onClick={handleCloseModal}
          />

          {/* Modal Content */}
          <div className="relative bg-gradient-to-br from-dark-surface/95 to-dark-bg/85 backdrop-blur-sm border border-dark-border/30 rounded-xl w-full max-w-lg shadow-2xl overflow-hidden">
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-dark-border/30 bg-gradient-to-r from-dark-bg via-dark-surface to-dark-bg">
              <h3 className="text-base font-semibold text-white">
                {currentImageUrl ? 'Change Event Image' : 'Add Event Image'}
              </h3>
              <button
                onClick={handleCloseModal}
                className="p-1.5 text-dark-muted hover:text-white hover:bg-dark-surface/50 rounded-lg transition-all duration-150"
              >
                <X size={18} />
              </button>
            </div>

            {/* Mode Tabs */}
            <div className="flex border-b border-dark-border/30">
              <button
                onClick={() => setMode('url')}
                className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 text-sm font-medium transition-all duration-150 ${
                  mode === 'url'
                    ? 'text-brand-400 border-b-2 border-brand-500 bg-brand-500/5'
                    : 'text-dark-muted hover:text-white hover:bg-dark-surface/50'
                }`}
              >
                <Link size={16} />
                Enter URL
              </button>
              <button
                onClick={() => setMode('file')}
                className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 text-sm font-medium transition-all duration-150 ${
                  mode === 'file'
                    ? 'text-brand-400 border-b-2 border-brand-500 bg-brand-500/5'
                    : 'text-dark-muted hover:text-white hover:bg-dark-surface/50'
                }`}
              >
                <Upload size={16} />
                Upload File
              </button>
            </div>

            {/* Content */}
            <div className="p-4 space-y-4">
              {mode === 'url' ? (
                /* URL Mode */
                <div className="space-y-3">
                  <label className="block text-sm text-dark-muted">
                    Paste an image URL
                  </label>
                  <input
                    type="url"
                    value={urlInput}
                    onChange={handleUrlChange}
                    placeholder="https://example.com/image.jpg"
                    className="w-full px-3 py-2.5 bg-dark-input border border-dark-border/30 rounded-xl text-white placeholder-dark-muted focus:outline-none focus:border-brand-500 min-h-[44px]"
                    autoFocus
                  />
                </div>
              ) : (
                /* File Upload Mode */
                <div
                  onDragEnter={handleDragEnter}
                  onDragLeave={handleDragLeave}
                  onDragOver={handleDragOver}
                  onDrop={handleDrop}
                  onClick={() => fileInputRef.current?.click()}
                  className={`border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition-all ${
                    isDragging
                      ? 'border-brand-500 bg-brand-500/10'
                      : 'border-dark-border/30 hover:border-gray-500 hover:bg-dark-surface/40/50'
                  }`}
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleFileInputChange}
                    className="hidden"
                  />
                  <Upload className="w-10 h-10 text-dark-muted mx-auto mb-3" />
                  <p className="text-white font-medium">
                    {isDragging ? 'Drop image here' : 'Drag & drop or click to upload'}
                  </p>
                  <p className="text-xs text-dark-muted mt-2">
                    PNG, JPG, GIF, WebP up to 5MB
                  </p>
                </div>
              )}

              {/* Preview */}
              {previewUrl && (
                <div className="space-y-2">
                  <label className="block text-sm text-dark-muted">Preview</label>
                  <div className="relative aspect-video rounded-lg overflow-hidden bg-dark-border">
                    <img
                      src={previewUrl}
                      alt="Preview"
                      className="w-full h-full object-contain"
                      onError={() => setError('Failed to load image preview')}
                    />
                  </div>
                </div>
              )}

              {/* Error Message */}
              {error && (
                <div className="flex items-center gap-2 p-3 bg-red-500/20 border border-red-500/50 rounded-lg text-red-300 text-sm">
                  <X size={16} className="flex-shrink-0" />
                  {error}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="flex items-center justify-between px-4 py-3 border-t border-dark-border/30 bg-gradient-to-r from-dark-bg via-dark-surface to-dark-bg">
              <div>
                {currentImageUrl && (
                  <button
                    onClick={handleRemoveImage}
                    disabled={isSaving}
                    className="flex items-center gap-1.5 px-3 py-2 text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded-lg transition-all duration-150 text-sm font-medium disabled:opacity-50"
                  >
                    <Trash2 size={14} />
                    Remove Image
                  </button>
                )}
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={handleCloseModal}
                  className="px-4 py-2 bg-dark-border text-white rounded-lg hover:bg-dark-surface/50 transition-all duration-150 text-sm font-medium min-h-[40px]"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  disabled={isSaving || (!urlInput.trim() && !previewUrl)}
                  className="flex items-center gap-1.5 px-4 py-2 bg-brand-500 text-white rounded-lg hover:bg-brand-600 transition-all duration-150 text-sm font-medium min-h-[40px] disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSaving ? (
                    <>
                      <Loader2 size={14} className="animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Check size={14} />
                      Save
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ImageUploadButton;
